import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Anchor, Award, Check, Copy, Gift, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TIER_STYLE = {
  bronze: {
    ring: "from-[#B87333] via-[#D9975B] to-[#F1BB7B]",
    bg: "bg-[#B87333]/15 border-[#D9975B]/40",
    text: "text-[#8A5A2A]",
    icon: Anchor,
  },
  silver: {
    ring: "from-[#95A5B4] via-[#B9C5D0] to-[#E1E7EC]",
    bg: "bg-[#95A5B4]/20 border-[#95A5B4]/50",
    text: "text-[#3A4756]",
    icon: Award,
  },
  gold: {
    ring: "from-[#BD984D] via-[#D9BB6D] to-[#F1DA96]",
    bg: "bg-[#BD984D]/20 border-[#BD984D]/50",
    text: "text-[#7A5F1E]",
    icon: Sparkles,
  },
};

/**
 * Captain loyalty card + checkout-ready reminder.
 * On checkout day the "Sjekk meg ut nå" button is featured prominently.
 * Once staff finalise the checkout checklist, a unique code is issued and
 * displayed here for reuse on the next booking.
 */
export default function CustomerCaptainCard({ lang, booking, onSignalled }) {
  const [status, setStatus] = useState(null);
  const [signalling, setSignalling] = useState(false);
  const T = (no, en) => (lang === "en" ? en : no);

  const load = async () => {
    try {
      const r = await api.get("/customer/captain-status");
      setStatus(r.data);
    } catch {
      setStatus(null);
    }
  };
  useEffect(() => { load(); }, [booking?.id]);

  const today = new Date().toISOString().slice(0, 10);
  const checkoutDate = booking?.end_date?.slice(0, 10);
  const isCheckoutDay = booking && checkoutDate === today;
  const alreadySignalled = !!booking?.customer_checkout_signal_at;
  const codeIssued = booking?.captain_code || null;

  const signalCheckout = async () => {
    if (!booking?.id) return;
    setSignalling(true);
    try {
      await api.post("/customer/checkout-signal", { booking_id: booking.id });
      toast.success(
        T(
          "Takk! Vi kommer og gjør båten klar. Kaptein-koden din er på vei.",
          "Thanks! We'll come and finalize the boat. Your captain code is on its way.",
        ),
      );
      onSignalled?.();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSignalling(false);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(T("Kode kopiert.", "Code copied."));
    } catch {
      toast.error(T("Kunne ikke kopiere.", "Could not copy."));
    }
  };

  const activeCode = status?.active_codes?.[0] || null;
  const tripCount = status?.trip_count || 0;
  const tierKey = status?.tier_key || null;
  const tierStyle = tierKey ? TIER_STYLE[tierKey] : null;
  const TierIcon = tierStyle?.icon || Anchor;

  // Progress toward next tier
  const nextTierAt = status?.next_tier_at;
  const progressPct = useMemo(() => {
    if (!nextTierAt) return 100;
    const prev = nextTierAt === 3 ? 1 : 3;
    return Math.round(
      ((Math.max(tripCount, prev) - prev) / (nextTierAt - prev)) * 100,
    );
  }, [nextTierAt, tripCount]);

  return (
    <Card
      data-testid="customer-captain-card"
      className="relative overflow-hidden p-6 sm:p-8 bg-gradient-to-br from-[#0C1824] via-[#11283A] to-[#0F1A2B] text-white border-0"
    >
      <div
        aria-hidden
        className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#BD984D]/15 blur-3xl pointer-events-none"
      />
      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#ECC974] font-semibold">
          <Award className="w-3.5 h-3.5" />
          {T("Kaptein-programmet", "Captain programme")}
        </div>

        {/* Checkout signal – only relevant on checkout day */}
        {isCheckoutDay && !codeIssued && (
          <div
            className={`mt-4 rounded-2xl border-2 border-dashed p-5 ${
              alreadySignalled
                ? "border-emerald-400/40 bg-emerald-400/10"
                : "border-[#BD984D]/60 bg-[#BD984D]/10 animate-pulse-slow"
            }`}
            data-testid="checkout-signal-panel"
          >
            {!alreadySignalled ? (
              <>
                <div className="text-[#ECC974] text-xs uppercase tracking-widest font-bold">
                  {T("Huskelapp for i dag", "Reminder for today")}
                </div>
                <h3 className="text-2xl font-bold mt-1">
                  {T(
                    "Trykk her når du sjekker ut – så får du en gave!",
                    "Tap here when you check out — get a gift!",
                  )}
                </h3>
                <p className="text-sm text-white/80 mt-2 max-w-xl leading-relaxed">
                  {T(
                    "Når du er ferdig på båten, trykk knappen så vi vet du er klar. Så snart teamet vårt har sjekket over båten, mottar du en unik Kaptein-kode med rabatt til neste tur.",
                    "When you're done with the boat, tap this button so we know you're ready. As soon as our team has finished the boat check, you'll receive a unique Captain code with a discount for your next trip.",
                  )}
                </p>
                <Button
                  onClick={signalCheckout}
                  disabled={signalling}
                  data-testid="checkout-signal-button"
                  className="mt-4 bg-[#BD984D] hover:bg-[#A57F39] text-[#0C1824] font-bold h-12 px-6 text-base"
                >
                  <Gift className="w-5 h-5 mr-2" />
                  {signalling
                    ? T("Sender…", "Sending…")
                    : T("Jeg sjekker ut nå", "I'm checking out now")}
                </Button>
              </>
            ) : (
              <div className="flex items-start gap-3">
                <div className="mt-1 h-8 w-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
                  <Check className="w-4 h-4 text-emerald-300" />
                </div>
                <div>
                  <div className="text-[#ECC974] text-xs uppercase tracking-widest font-bold">
                    {T("Vi er på saken", "We're on it")}
                  </div>
                  <h3 className="text-xl font-bold mt-1">
                    {T(
                      "Takk! Vi klargjør båten – kaptein-koden er på vei.",
                      "Thanks! We're finalizing the boat — your captain code is on the way.",
                    )}
                  </h3>
                  <p className="text-sm text-white/80 mt-1">
                    {T(
                      "Du får en push-varsling så snart koden din er klar.",
                      "You'll get a push notification once your code is ready.",
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Active code preview */}
        {(codeIssued || activeCode) && (
          <div
            className={`mt-4 rounded-2xl border p-5 ${tierStyle?.bg || "bg-white/5 border-white/10"}`}
            data-testid="captain-code-panel"
          >
            <div className="flex items-start gap-4 flex-wrap">
              <div
                className={`shrink-0 h-16 w-16 rounded-full bg-gradient-to-br ${tierStyle?.ring || "from-white/20 to-white/5"} flex items-center justify-center border-2 border-white/40 shadow-lg`}
              >
                <TierIcon className="w-8 h-8 text-white drop-shadow" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#ECC974] font-semibold">
                  {T("Ditt kaptein-nivå", "Your captain rank")}
                </div>
                <div className="text-2xl font-bold mt-0.5">
                  {activeCode?.tier_label || status?.tier_label}
                </div>
                <div className="text-xs text-white/70 mt-0.5">
                  {T("Antall turer", "Trips completed")}: <span className="font-bold text-white">{tripCount}</span>
                </div>
              </div>
              <div className="w-full sm:w-auto sm:min-w-[220px] bg-black/30 rounded-xl px-4 py-3 border border-white/10">
                <div className="text-[10px] uppercase tracking-widest text-white/60">
                  {T("Din unike kode", "Your unique code")}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <code
                    data-testid="captain-code-value"
                    className="font-mono text-lg tracking-widest text-[#ECC974] flex-1 select-all"
                  >
                    {activeCode?.code || codeIssued}
                  </code>
                  <button
                    onClick={() => copyCode(activeCode?.code || codeIssued)}
                    data-testid="captain-code-copy"
                    aria-label={T("Kopier kode", "Copy code")}
                    className="text-white/70 hover:text-white p-1"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-[10px] text-white/60 mt-1">
                  {T("Rabatt neste tur", "Next-trip discount")}:{" "}
                  <span className="font-bold text-emerald-300">
                    {activeCode?.discount_pct ?? 5}%
                  </span>
                </div>
              </div>
            </div>
            {activeCode?.perks?.length > 0 && (
              <ul className="mt-4 space-y-1.5 text-sm text-white/85">
                {activeCode.perks.map((p, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 text-[#ECC974] shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Just-getting-started state */}
        {tripCount === 0 && !isCheckoutDay && !codeIssued && (
          <div className="mt-4 text-white/80 text-sm leading-relaxed max-w-xl">
            {T(
              "Bli med i vårt kaptein-programmet – fullfør turen din, og få en unik kode med rabatt til din neste booking. Jo flere turer, desto høyere rang.",
              "Join our captain programme — complete your trip and get a unique code with a discount for your next booking. The more trips, the higher the rank.",
            )}
          </div>
        )}

        {/* Progress to next tier */}
        {nextTierAt && tripCount > 0 && (
          <div className="mt-4">
            <div className="flex justify-between text-[10px] uppercase tracking-widest text-white/60 mb-1">
              <span>
                {tripCount} {T("turer", "trips")}
              </span>
              <span>
                {T("Neste rang", "Next rank")}: {nextTierAt}
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#BD984D] to-[#ECC974] transition-all"
                style={{ width: `${Math.min(100, Math.max(6, progressPct))}%` }}
                data-testid="captain-progress-bar"
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
